import { useToasts } from '@/hooks/useToasts'
import { useSelector } from 'react-redux'

import {
  useCreateCheckpointMutation,
  useFloorPlanDetailsQuery,
} from '@/graphql/generated/hooks'
import { DeviceType } from '@/graphql/generated/schemas'
import { selectMe } from '@/redux/me/meSlice'
import { mixpanel } from '@/utils/analytics'

interface UseCreateCheckpointIProps {
  floorId: string
  onClose: () => void
}

export const useCreateCheckpoint = ({
  floorId,
  onClose,
}: UseCreateCheckpointIProps) => {
  const me = useSelector(selectMe)
  const { showSuccess, showError } = useToasts()
  const { data, loading: isDataLoading } = useFloorPlanDetailsQuery({
    variables: { floorId },
  })
  const [createCheckpoint, { loading: isCreateDeviceLoading }] =
    useCreateCheckpointMutation()

  const create = async (name: string) => {
    try {
      await createCheckpoint({
        variables: {
          input: {
            name: name,
            facilityId: data?.floor?.facility?.id,
            floorId: floorId,
            type: DeviceType?.Checkpoint,
          },
        },
        refetchQueries: [
          'FloorDevicesCount',
          'FacilityDevicesByFloors',
          'FloorsFacilityCheckpoints',
        ],
      })
      showSuccess(`Successfully created ${name} Checkpoint.`)
      onClose()
      mixpanel.track(
        'Button Press: Facility Checkpoints - Create Checkpoint - Create',
        {
          facility_id: data?.floor?.facility?.id,
          role: me.roleName,
        }
      )
    } catch {
      showError(`Error creating ${name} Checkpoint`)
    }
  }

  const handleOnClose = () => {
    onClose()
    mixpanel.track(
      'Button Press: Facility Checkpoints - Create Checkpoint - Cancel',
      {
        facility_id: data?.floor?.facility?.id,
        role: me.roleName,
      }
    )
  }

  return {
    facilityName: data?.floor?.facility?.name,
    isDataLoading,
    isCreateDeviceLoading,
    create,
    handleOnClose,
  }
}
